import { setAlerts } from './set-alerts';
import { ApiClient } from './api-client';

declare const DataTable: any;
declare const bootstrap: any;

// Helper to toggle spinner
function toggleSpin(btnSelector: string | HTMLElement, isLoading: boolean, text: string) {
    const btn = (typeof btnSelector === 'string' ? document.querySelector(btnSelector) : btnSelector) as HTMLButtonElement;
    if (!btn) return;
    
    if (isLoading) {
        btn.disabled = true;
        btn.innerHTML = `<i class='fas fa-spinner fa-spin'></i>&nbsp${text}`;
    } else {
        btn.disabled = false;
        btn.innerHTML = text;
    }
}

function serializeFormToJson(form: HTMLFormElement): Record<string, any> {
    const formData = new FormData(form);
    const obj: Record<string, any> = {};
    
    formData.forEach((value, key) => {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (Array.isArray(obj[key])) {
                obj[key].push(value);
            } else {
                obj[key] = [obj[key], value];
            }
        } else {
            obj[key] = value;
        }
    });

    const indexed: Record<string, Record<number, Record<string, any>>> = {};
    const bracketKeyPattern = /^(\w+)\[(\d+)\]\[(.+)\]$/;
    
    for (const key of Object.keys(obj)) {
        const match = key.match(bracketKeyPattern);
        if (!match) continue;
        const [, group, indexStr, remainder] = match;
        const index = parseInt(indexStr, 10);
        if (!Number.isFinite(index)) continue;
        
        const segments = remainder.split('][');
        const prop = segments[segments.length - 1];
        
        indexed[group] = indexed[group] || {};
        indexed[group][index] = indexed[group][index] || {};
        indexed[group][index][prop] = obj[key];
        delete obj[key];
    }

    for (const group of Object.keys(indexed)) {
        const arr: Record<string, any>[] = [];
        const keys = Object.keys(indexed[group]).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
        for (const k of keys) {
            arr.push(indexed[group][parseInt(k, 10)]);
        }
        if (arr.length > 0) obj[group] = arr;
    }
    
    return obj;
}

function coerceBooleanStrings(obj: Record<string, any>) {
    for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (v === 'true') obj[k] = true;
        else if (v === 'false') obj[k] = false;
    }
}

function coerceNumericAttributes(obj: Record<string, any>, form?: HTMLFormElement) {
    if (!form) return;
    const inputs = form.querySelectorAll('[data-type]');
    inputs.forEach((el) => {
        const input = el as HTMLInputElement | HTMLSelectElement;
        const name = input.name;
        const typeAttr = (input.getAttribute('data-type') || '').toLowerCase();
        
        if (!name || !typeAttr) return;
        if (!Object.prototype.hasOwnProperty.call(obj, name)) return;
        
        const raw = obj[name];
        if (typeof raw !== 'string') return;
        if (raw.trim() === '') { obj[name] = null; return; }
        if (!/^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(raw)) return;
        
        if (typeAttr === 'int') {
            obj[name] = parseInt(raw, 10);
        } else if (typeAttr === 'float' || typeAttr === 'number') {
            obj[name] = parseFloat(raw);
        }
    });
}

export async function showAdd(parentSelector: string, onShow?: () => Promise<void>, addFormSelector = '#add-form') {
    const parent = document.querySelector(parentSelector);
    if (!parent) return;

    const form = document.querySelector(`${parentSelector} ${addFormSelector}`) as HTMLFormElement;
    if (form) {
        form.classList.remove('was-validated');
        form.reset();
    }

    if (onShow) {
        try { await onShow(); } catch (err: any) {
            if (err?.message) setAlerts('danger', err.message); 
            else setAlerts('danger', `${err.status || 'Error'} (${err.statusText || ''}): ${err.responseText || err}`);
            
            const modal = bootstrap.Modal.getOrCreateInstance(parent);
            modal.hide();
            return;
        }
    }
    const modal = bootstrap.Modal.getOrCreateInstance(parent);
    modal.show();
}

export async function saveAdd(
    url: string,
    prepareData?: (form: HTMLFormElement) => Promise<Record<string, any> | FormData>,
    parentSelector: string = '#add-dialog',
    dataTableSelector: string = '#table',
    reload: boolean | (() => void) = true,
    addFormSelector: string = '#add-form',
    successMsg: string = 'Change(s) successfully applied',
    asJson: boolean = true
) {
    const parent = document.querySelector(parentSelector);
    if (!parent) return;

    const form = document.querySelector(`${parentSelector} ${addFormSelector}`) as HTMLFormElement;
    if (!form.checkValidity()) {
        form.classList.add('was-validated');
        return;
    }

    const saveBtn = parent.querySelector('#btn-add-save') as HTMLButtonElement;
    const cancelBtn = parent.querySelector('#btn-add-cancel') as HTMLButtonElement;
    const inputs = form.querySelectorAll('input, select, textarea');

    toggleSpin(saveBtn, true, 'Saving...');
    if (cancelBtn) cancelBtn.disabled = true;
    inputs.forEach((el: any) => el.readOnly = true);

    try {
        let data: any;
        if (prepareData) {
            data = await prepareData(form);
        } else {
            if (asJson) {
                data = serializeFormToJson(form);
                coerceBooleanStrings(data);
                coerceNumericAttributes(data, form);
            } else {
                data = new FormData(form);
            }
        }

        if (asJson) {
            await ApiClient.post(url, data);
        } else {
            if (data instanceof FormData) {
                 const params = new URLSearchParams(data as any);
                 await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                    body: params
                });
            } else {
                 await ApiClient.post(url, data);
            }
        }

        const modal = bootstrap.Modal.getOrCreateInstance(parent);
        modal.hide();
        setAlerts('success', successMsg);
        
        if (typeof reload === 'function') {
            reload();
        } else if (reload) {
            new DataTable(dataTableSelector, { retrieve: true }).ajax.reload();
        }
        
        form.reset();

    } catch (err: any) {
        if (err?.message) setAlerts('danger', err.message);
        else setAlerts('danger', `${err.status || 'Error'}: ${err.responseText || err}`);
    } finally {
        toggleSpin(saveBtn, false, 'Save');
        if (cancelBtn) cancelBtn.disabled = false;
        inputs.forEach((el: any) => el.readOnly = false);
    }
}

export async function saveEdit(
    url: string,
    prepareData?: (form: HTMLFormElement) => Promise<Record<string, any> | FormData>,
    parentSelector: string = '#edit-dialog',
    dataTableSelector: string = '#table',
    reload: boolean | (() => void) = true,
    editFormSelector: string = '#edit-form',
    successMsg: string = 'Change(s) successfully applied',
    asJson: boolean = true
) {
    const parent = document.querySelector(parentSelector);
    if (!parent) return;

    const form = document.querySelector(`${parentSelector} ${editFormSelector}`) as HTMLFormElement;
    if (!form.checkValidity()) {
        form.classList.add('was-validated');
        return;
    }

    const saveBtn = parent.querySelector('#btn-edit-save') as HTMLButtonElement;
    const cancelBtn = parent.querySelector('#btn-edit-cancel') as HTMLButtonElement;
    const inputs = form.querySelectorAll('input, select, textarea');

    toggleSpin(saveBtn, true, 'Saving...');
    if (cancelBtn) cancelBtn.disabled = true;
    inputs.forEach((el: any) => el.readOnly = true);

    try {
        let data: any;
        if (prepareData) {
            data = await prepareData(form);
        } else {
            if (asJson) {
                data = serializeFormToJson(form);
                coerceBooleanStrings(data);
                coerceNumericAttributes(data, form);
            } else {
                data = new FormData(form);
            }
        }

        let responseData: any;
        if (asJson) {
            responseData = await ApiClient.put(url, data);
        } else {
            if (data instanceof FormData) {
                 const params = new URLSearchParams(data as any);
                 const response = await fetch(url, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                    body: params
                });
                if (response.ok) {
                    try {
                        responseData = await response.json();
                    } catch {
                        responseData = true; // Success but no JSON
                    }
                } else {
                    throw new Error(response.statusText);
                }
            } else {
                 responseData = await ApiClient.put(url, data);
            }
        }

        const modal = bootstrap.Modal.getOrCreateInstance(parent);
        modal.hide();
        setAlerts('success', successMsg);
        
        if (typeof reload === 'function') {
            reload();
        } else if (reload) {
            new DataTable(dataTableSelector, { retrieve: true }).ajax.reload();
        }
        
        form.reset();
        return responseData;

    } catch (err: any) {
        if (err?.message) setAlerts('danger', err.message);
        else setAlerts('danger', `${err.status || 'Error'}: ${err.responseText || err}`);
    } finally {
        toggleSpin(saveBtn, false, 'Save');
        if (cancelBtn) cancelBtn.disabled = false;
        inputs.forEach((el: any) => el.readOnly = false);
    }
}

export async function saveDelete(
    url: string,
    parentSelector: string = '#delete-dialog',
    dataTableSelector: string = '#table',
    reload: boolean | (() => void) = true,
    successMsg: string = 'Change(s) successfully applied'
) {
    const parent = document.querySelector(parentSelector);
    if (!parent) return;

    const deleteBtn = parent.querySelector('#btn-delete') as HTMLButtonElement;
    const cancelBtn = parent.querySelector('#btn-delete-cancel') as HTMLButtonElement;

    toggleSpin(deleteBtn, true, 'Deleting...');
    if (cancelBtn) cancelBtn.disabled = true;

    try {
        await ApiClient.delete(url);

        const modal = bootstrap.Modal.getOrCreateInstance(parent);
        modal.hide();
        setAlerts('success', successMsg);
        
        if (typeof reload === 'function') {
            reload();
        } else if (reload) {
            new DataTable(dataTableSelector, { retrieve: true }).ajax.reload();
        }

    } catch (err: any) {
        if (err?.message) setAlerts('danger', err.message);
        else setAlerts('danger', `${err.status || 'Error'}: ${err.responseText || err}`);
    } finally {
        toggleSpin(deleteBtn, false, 'Delete');
        if (cancelBtn) cancelBtn.disabled = false;
    }
}

export function processFormCheckBox(formData: FormData, name: string) {
    if (!formData.has(name)) {
        formData.append(name, 'false');
    }
}
